import { FunctionComponent, useState } from "react";

import { FeedCompany } from "@signalist/shared-types";

import { Organization } from "@/pages/Private/pages/Apollo/schema/get-people";
import { ContactProspectDetail } from "@/pages/Private/pages/Contacts/schema/contact-detail";
import { CompanyHeader } from "@/pages/Private/pages/ProfileTracker/components/CustomColumns/CompanyHeader";

/**
 * Stand-in organization built from the card's own (lean) company while the
 * prospect detail loads — and the permanent source on degraded company-only
 * cards, where no prospect detail exists to upgrade from.
 */
const buildOrganizationPreview = (company: FeedCompany): Organization =>
	({
		id: company.organizationId ?? 0,
		name: company.name,
		logoUrl: company.logoUrl,
		linkedinUrl: company.linkedinUrl,
		websiteUrl: company.websiteUrl,
		industry: company.industry,
		estimatedNumEmployees: company.employeeCount,
		city: company.location,
	}) as unknown as Organization;

interface FeedCompanyIdentityProps {
	company: FeedCompany;
	/** Prospect detail's organization once fetched — richer than the card's. */
	organization?: ContactProspectDetail["organization"];
}

/**
 * Company box of a run card: the shared lead-panel company card
 * ({@link CompanyHeader}), fed by the prospect detail's organization and
 * falling back to the card's own company fields until (or unless) it loads.
 */
export const FeedCompanyIdentity: FunctionComponent<FeedCompanyIdentityProps> = ({
	company,
	organization,
}) => {
	const [companyLogoError, setCompanyLogoError] = useState(false);

	return (
		<div className="min-w-0 flex-1" data-testid="feed-company-box">
			<CompanyHeader
				className="flex h-full flex-col"
				companyLogoError={companyLogoError}
				organization={
					(organization as unknown as Organization | undefined) ?? buildOrganizationPreview(company)
				}
				setCompanyLogoError={setCompanyLogoError}
				showFindMoreLeads={false}
				trackerOrganizationName={company.name ?? undefined}
			/>
		</div>
	);
};
